import type {
  ChartDatum,
  ExperienceUsageData,
  MetricType,
} from "./utils/usageUtils";

export interface CountryUsageCardProps {
  /** Processed country rows from useCountrySessionsData / useCountryHoursData */
  rows: CountryMetricRow[];
  /** Card title */
  title: string;
  /** Bar colour — use theme.colors.metrics.* */
  barColor: string;
  /** Whether a background refetch is happening */
  isFetching?: boolean;
  /** Whether the initial load is in progress */
  isLoading?: boolean;
  /** True when there is no data at all */
  isEmpty?: boolean;
  /** True when the request failed — distinct from "no data for this period" */
  isError?: boolean;
  /** When true, thresholds are removed and all values are shown */
  isUserLevelDataEnabled?: boolean;
  /** Start of the selected date range */
  startDate: Date;
  /** End of the selected date range */
  endDate: Date;
}

export interface UsagePerExperienceProps {
  data: ExperienceUsageData[];
  isLoading?: boolean;
  isFetching?: boolean;
  showCertifications?: boolean;
  isUserLevelDataEnabled?: boolean;
  /** Start of the selected date range */
  startDate: Date;
  /** End of the selected date range */
  endDate: Date;
}

export interface CustomTickProps {
  x?: number | string;
  y?: number | string;
  payload?: { value?: string };
  fill?: string;
}

export interface CustomTooltipPayloadItem {
  dataKey: MetricType;
  value: number;
  color: string;
  name: string;
  payload: ChartDatum;
}

export interface UsageYAxisTickProps {
  x?: number;
  y?: number;
  payload?: { value: number };
  fillColor: string;
  fontFamily?: string;
  useLogScale?: boolean;
}

export interface CustomBarShapeProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: ChartDatum;
  fillKey: keyof ChartDatum;
  defaultFill: string;
}

export interface CalendarViewProps {
  tempStartDate: Date | null;
  tempEndDate: Date | null;
  setTempStartDate: (date: Date | null) => void;
  setTempEndDate: (date: Date | null) => void;
  earliestSelectableDate?: Date;
}

/**
 * Generic display row used by both country-metric cards.
 * The `metricValue` field holds whichever metric the card is visualising
 * (session count, VR hours, etc.).
 */
export interface CountryMetricRow {
  /** Display name shown in the card (e.g. "Germany") */
  name: string;
  /** Absolute metric value (sessions, hours, …) */
  metricValue: number;
  /** Percentage of total metric (0-100), or -1 for "N/A" */
  percentage: number;
  /** Whether this is the special "Other" bucket */
  isOther: boolean;
  /** Whether this row represents sessions with no captured country information */
  isUnknown?: boolean;
  /** For the "Other" row: the individual countries grouped into it */
  otherCountries?: { name: string; metricValue: number }[];
}

// ── Feedback Rating Types ───────────────────────────────────────────

export const FeedbackMetricType = {
  PRACTICE: "practice",
  ASSESSMENT: "assessment",
} as const;

export type FeedbackMetricType =
  (typeof FeedbackMetricType)[keyof typeof FeedbackMetricType];

export interface ExperienceFeedbackRating {
  /** Unique content metadata id — used to key the chart so duplicate names stay distinct */
  experienceId?: string;
  experienceName: string;
  practiceRating: number | null;
  assessmentRating: number | null;
  practiceFeedbackCount: number;
  assessmentFeedbackCount: number;
}

export interface FeedbackOverallRating {
  averageRating: number | null;
  totalFeedbackCount: number;
}

export interface FeedbackRatingsCardProps {
  data: ExperienceFeedbackRating[];
  overallRating: FeedbackOverallRating;
  isLoading?: boolean;
  isFetching?: boolean;
}

export interface FeedbackChartDatum {
  id: string;
  name: string;
  practice: number;
  assessment: number;
  practiceCount: number;
  assessmentCount: number;
}

export interface FeedbackTooltipPayloadItem {
  dataKey: FeedbackMetricType;
  value: number;
  color: string;
  name: string;
  payload: FeedbackChartDatum;
}
